import React, { useMemo } from 'react';
import { Box, Typography, Grid, LinearProgress, Chip, Card, CardContent, CardHeader, Avatar, List, ListItem, ListItemText, ListItemAvatar, Button, Badge, Divider } from '@mui/material';
import { People, Assignment, TrendingUp, Warning, CheckCircle, Schedule, Task, Assessment, School, EmojiEvents, Lightbulb, Help, Message, CalendarToday, Speed, Security, CloudDone, Announcement } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
// charts imports removed (not currently used in CI build)
import { useQuery } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

type PerfUser = {
  id: number;
  full_name: string;
  role: 'admin' | 'supervisor' | 'student';
  tasks_assigned?: number;
  tasks_completed?: number;
  on_time_rate?: string;
  last_active?: string | null;
  department?: string;
  activity_level?: number[];
};

type PerfOverviewResponse = { performanceData: PerfUser[] };

// function pct(n: number) {
//   return Math.max(0, Math.min(100, Math.round(n)));
// }

function averageCompletion(users: PerfUser[]) {
  const withTasks = users.filter(u => (u.tasks_assigned || 0) > 0);
  if (!withTasks.length) return 0;
  const sum = withTasks.reduce((s, u) => s + ((u.tasks_completed || 0) / (u.tasks_assigned || 1)) * 100, 0);
  return sum / withTasks.length;
}

function averageOnTime(users: PerfUser[]) {
  if (!users.length) return 0;
  const sum = users.reduce((s, u) => s + parseFloat(u.on_time_rate || '0'), 0);
  return sum / users.length;
}

// Enhanced Admin Dashboard Component
function AdminDashboard({ users }: { users: PerfUser[] }) {
  const navigate = useNavigate();
  const exportReport = () => {
    const rows = users.map(u => ({
      ID: u.id,
      Name: u.full_name,
      Role: u.role,
      Tasks_Assigned: u.tasks_assigned ?? 0,
      Tasks_Completed: u.tasks_completed ?? 0,
      Completion_Rate: u.on_time_rate ?? '0.0',
      Last_Active: u.last_active || ''
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Performance');
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const dateStr = new Date().toISOString().split('T')[0];
    saveAs(blob, `performance-report-${dateStr}.xlsx`);
  };
  const visible = useMemo(() => users.filter(u => u.role !== 'admin'), [users]);
  const supervisors = useMemo(() => visible.filter(u => u.role === 'supervisor'), [visible]);
  const students = useMemo(() => visible.filter(u => u.role === 'student'), [visible]);
  
  const systemStats = useMemo(() => ({
    totalUsers: visible.length,
    supervisors: supervisors.length,
    students: students.length,
    activeUsers: visible.filter(u => u.last_active && new Date(u.last_active) > new Date(Date.now() - 24 * 60 * 60 * 1000)).length,
    avgProgress: averageCompletion(visible),
    avgOnTime: averageOnTime(visible),
  }), [visible, supervisors, students]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          System Command Center
        </Typography>
        {/* Actions removed per request */}
      </Box>

      {/* System Statistics Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {systemStats.totalUsers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Users
                  </Typography>
                </Box>
                <People sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {systemStats.activeUsers}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active (24h)
                  </Typography>
                </Box>
                <Speed sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Math.round(systemStats.avgProgress)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg Progress
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
              <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Math.round(systemStats.avgOnTime)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    On-time Rate
                    </Typography>
                  </Box>
                <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* System Health & Alerts */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="System Health" />
            <CardContent>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <CloudDone color="success" sx={{ mr: 1 }} />
                  <Typography variant="body2">System Status</Typography>
                </Box>
                <LinearProgress variant="determinate" value={100} color="success" />
                <Typography variant="caption" color="success.main">All systems operational</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Security color="info" sx={{ mr: 1 }} />
                  <Typography variant="body2">Security</Typography>
                </Box>
                <LinearProgress variant="determinate" value={95} color="info" />
                <Typography variant="caption" color="info.main">Secure</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>Recent Alerts</Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'success.main', width: 24, height: 24 }}>
                      <CheckCircle fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="System backup completed"
                    secondary="2 hours ago"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main', width: 24, height: 24 }}>
                      <Warning fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary="High memory usage detected"
                    secondary="4 hours ago"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* User Activity Timeline */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="Recent User Activity" />
            <CardContent>
              <List dense>
                {visible.slice(0, 5).map((user, index) => (
                  <ListItem key={user.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: user.role === 'supervisor' ? 'warning.main' : 'info.main' }}>
                        {user.full_name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={user.full_name}
                      secondary={`${user.role} • Last active: ${user.last_active ? new Date(user.last_active).toLocaleDateString() : 'Never'}`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip 
                      size="small" 
                      label={user.role} 
                      color={user.role === 'supervisor' ? 'warning' : 'info'} 
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="Quick Actions" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth 
                    startIcon={<People />}
                    sx={{ height: 60 }}
                    onClick={() => navigate('/users')}
                  >
                    Manage Users
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Assessment />}
                    sx={{ height: 60 }}
                    onClick={exportReport}
                  >
                    Generate Report
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth 
                    startIcon={<Announcement />}
                    sx={{ height: 60 }}
                    onClick={() => navigate('/announcements')}
                  >
                    Announcements
                  </Button>
                </Grid>
                {/* System Settings button removed per request */}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// Enhanced Supervisor Dashboard Component
function SupervisorDashboard({ users }: { users: PerfUser[] }) {
  const navigate = useNavigate();
  const students = useMemo(() => users.filter(u => u.role === 'student'), [users]);

  const studentStats = useMemo(() => ({
    totalStudents: students.length,
    activeStudents: students.filter(u => u.last_active && new Date(u.last_active) > new Date(Date.now() - 24 * 60 * 60 * 1000)).length,
    avgProgress: averageCompletion(students),
    avgOnTime: averageOnTime(students),
    atRiskStudents: students.filter(u => {
      const progress = (u.tasks_completed || 0) / (u.tasks_assigned || 1);
      return progress < 0.3 && (u.tasks_assigned || 0) > 0;
    }).length,
  }), [students]);

  const topPerformers = useMemo(() => 
    students
      .filter(u => (u.tasks_completed || 0) > 0)
      .sort((a, b) => (b.tasks_completed || 0) - (a.tasks_completed || 0))
      .slice(0, 3),
    [students]
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Student Success Hub
        </Typography>
        {/* Top right actions removed per request */}
      </Box>

      {/* Student Statistics Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {studentStats.totalStudents}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Students
                  </Typography>
                </Box>
                <School sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {studentStats.activeStudents}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Today
                  </Typography>
                </Box>
                <Speed sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Math.round(studentStats.avgProgress)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg Progress
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {studentStats.atRiskStudents}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Need Attention
                  </Typography>
                </Box>
                <Warning sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Top Performers & At-Risk Students */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="Student Highlights" />
            <CardContent>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'success.main', fontWeight: 'bold' }}>
                🏆 Top Performers
              </Typography>
              <List dense>
                {topPerformers.map((student, index) => (
                  <ListItem key={student.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'success.main' }}>
                        <EmojiEvents />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={student.full_name}
                      secondary={`${student.tasks_completed || 0} tasks completed`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                ⚠️ Need Attention
              </Typography>
              <List dense>
                {users
                  .filter(u => {
                    const progress = (u.tasks_completed || 0) / (u.tasks_assigned || 1);
                    return progress < 0.3 && (u.tasks_assigned || 0) > 0;
                  })
                  .slice(0, 3)
                  .map((student) => (
                    <ListItem key={student.id}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'warning.main' }}>
                          <Warning />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={student.full_name}
                        secondary={`${Math.round(((student.tasks_completed || 0) / (student.tasks_assigned || 1)) * 100)}% progress`}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Communication Center */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="Communication Center" />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
              <Button
                variant="outlined"
                fullWidth
                    startIcon={<Message />}
                    sx={{ height: 60 }}
                    onClick={() => navigate('/chat')}
              >
                    Send Message
              </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<CalendarToday />}
                    sx={{ height: 60 }}
                    onClick={() => navigate('/meetings')}
                  >
                    Schedule Meeting
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<Assessment />}
                    sx={{ height: 60 }}
                  >
                    Grade Assignments
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// Enhanced Student Dashboard Component
function StudentDashboard({ users }: { users: PerfUser[] }) {
  const { user: authUser } = useAuth();
  const personalStats = useMemo(() => {
    const myData = (authUser && users.find(u => u.id === authUser.id))
      || users.find(u => u.role === 'student')
      || users[0];
    return {
      myProgress: (myData?.tasks_completed || 0) / (myData?.tasks_assigned || 1) * 100,
      myOnTime: parseFloat(myData?.on_time_rate || '0'),
      teamProgress: averageCompletion(users),
      teamOnTime: averageOnTime(users),
      myTasks: myData?.tasks_completed || 0,
      myTotalTasks: myData?.tasks_assigned || 0,
    };
  }, [users, authUser]);

  const teamMembers = useMemo(() => 
    users.filter(u => u.role === 'student').slice(1, 4),
    [users]
  );

  // Dynamic learning goals based on actual user data
  const learningGoals = useMemo(() => {
    const myData = (authUser && users.find(u => u.id === authUser.id))
      || users.find(u => u.role === 'student')
      || users[0];
    
    const tasksCompleted = myData?.tasks_completed || 0;
    const tasksAssigned = myData?.tasks_assigned || 0;
    const onTimeRate = parseFloat(myData?.on_time_rate || '0');
    
    return [
      { 
        id: 'weekly', 
        title: tasksAssigned > 0 ? `Complete ${tasksAssigned} tasks this week` : 'Set your first goal', 
        progress: tasksAssigned > 0 ? Math.round((tasksCompleted / tasksAssigned) * 100) : 0, 
        target: tasksAssigned || 1, 
        current: tasksCompleted 
      },
      { 
        id: 'monthly', 
        title: 'Maintain 90% on-time rate', 
        progress: Math.round(onTimeRate), 
        target: 90, 
        current: Math.round(onTimeRate) 
      },
      { 
        id: 'skill', 
        title: 'Learn new technology', 
        progress: tasksCompleted > 0 ? Math.min(100, tasksCompleted * 20) : 0, 
        target: 100, 
        current: tasksCompleted > 0 ? Math.min(100, tasksCompleted * 20) : 0 
      },
    ];
  }, [users, authUser]);

  const ComingSoonTile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box sx={{ position: 'relative' }}>
      <Box sx={{ filter: 'blur(1.5px)', pointerEvents: 'none' }}>
        {children}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.25)',
          borderRadius: 1,
        }}
      >
        <Chip color="warning" label="Coming Soon" />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Personal Learning Center
        </Typography>
        {/* Top right actions removed per request */}
      </Box>

      {/* Personal Progress Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {personalStats.myTasks}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    My Tasks Done
                  </Typography>
                </Box>
                <Task sx={{ fontSize: 40, opacity: 0.8 }} />
            </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Math.round(personalStats.myProgress)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    My Progress
            </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {Math.round(personalStats.myOnTime)}%
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    On-time Rate
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
      </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {personalStats.myTotalTasks}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Tasks
            </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, opacity: 0.8 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* My Progress & Goals */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="My Progress & Goals" />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Current Goal</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ flexGrow: 1 }}>
                    {learningGoals[0].title}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {learningGoals[0].current}/{learningGoals[0].target}
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={learningGoals[0].progress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>

              <Typography variant="subtitle2" gutterBottom>Learning Goals</Typography>
              <List dense>
                {learningGoals.map((goal) => (
                  <ListItem key={goal.id}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: goal.progress >= 80 ? 'success.main' : goal.progress >= 50 ? 'warning.main' : 'error.main' }}>
                        {goal.progress >= 80 ? <CheckCircle /> : <Schedule />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={goal.title}
                      secondary={`${goal.progress}% complete`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Team Collaboration */}
        <Grid item xs={12} lg={6}>
          <Card sx={{ height: 400 }}>
            <CardHeader title="Team Collaboration" />
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>Team Members</Typography>
              <List dense>
                {teamMembers.map((member) => (
                  <ListItem key={member.id}>
                    <ListItemAvatar>
                      <Badge 
                        color="success" 
                        variant="dot" 
                        invisible={!member.last_active || new Date(member.last_active) < new Date(Date.now() - 24 * 60 * 60 * 1000)}
                      >
                      <Avatar sx={{ bgcolor: 'info.main' }}>
                          {member.full_name.charAt(0)}
                      </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={member.full_name}
                      secondary={`${member.tasks_completed || 0} tasks completed`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    <Chip 
                      size="small" 
                      label={`${Math.round(((member.tasks_completed || 0) / (member.tasks_assigned || 1)) * 100)}%`}
                      color={member.tasks_completed === member.tasks_assigned ? 'success' : 'default'}
                    />
                  </ListItem>
                ))}
                </List>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Team Performance</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Team Progress</Typography>
                <Typography variant="body2" color="primary">
                  {Math.round(personalStats.teamProgress)}%
                            </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={personalStats.teamProgress} 
                sx={{ height: 6, borderRadius: 3, mb: 2 }}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Team On-time Rate</Typography>
                <Typography variant="body2" color="primary">
                  {Math.round(personalStats.teamOnTime)}%
                            </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={personalStats.teamOnTime} 
                sx={{ height: 6, borderRadius: 3 }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* My Tasks & Learning Resources */}
        <Grid item xs={12} md={6}>
          <ComingSoonTile>
            <Card sx={{ height: 300 }}>
              <CardHeader title="My Tasks & Assignments" />
              <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Task />}
                    sx={{ height: 60 }}
                  >
                    View Tasks
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<CalendarToday />}
                    sx={{ height: 60 }}
                  >
                    Calendar
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Assignment />}
                    sx={{ height: 60 }}
                  >
                    Submit Work
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button 
                    variant="outlined" 
                    fullWidth 
                    startIcon={<Assessment />}
                    sx={{ height: 60 }}
                  >
                    View Grades
                  </Button>
                </Grid>
              </Grid>
              </CardContent>
            </Card>
          </ComingSoonTile>
        </Grid>

        <Grid item xs={12} md={6}>
          <ComingSoonTile>
            <Card sx={{ height: 300 }}>
              <CardHeader title="Learning Resources" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      startIcon={<Lightbulb />}
                      sx={{ height: 60 }}
                    >
                      Tutorials
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      startIcon={<School />}
                      sx={{ height: 60 }}
                    >
                      Course Materials
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      startIcon={<Help />}
                      sx={{ height: 60 }}
                    >
                      Help Desk
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button 
                      variant="outlined" 
                      fullWidth 
                      startIcon={<Message />}
                      sx={{ height: 60 }}
                    >
                      Ask Question
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </ComingSoonTile>
        </Grid>
      </Grid>
    </Box>
  );
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data } = useQuery<PerfOverviewResponse>('dashboard-data', async () => {
    const res = await api.get('/performance/overview');
    return res.data;
  }, { refetchOnMount: 'always', staleTime: 0 });

  const allUsers = data?.performanceData || [];

  return (
    <Box sx={{ width: '100%' }}>
      {user?.role === 'admin' && <AdminDashboard users={allUsers} />}
      {user?.role === 'supervisor' && <SupervisorDashboard users={allUsers} />}
      {user?.role === 'student' && <StudentDashboard users={allUsers} />}
    </Box>
  );
};

export default Dashboard;